import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {NG_VALIDATORS, NG_VALUE_ACCESSOR} from '@angular/forms';
import {BaseControlDirective} from '../../directives/base-control.directive';
import {fadeInOut} from '../../animations/animations';
import {OptionsListComponent} from '../options-list/options-list.component';

@Component({
  selector: 'app-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  animations: [fadeInOut],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DropdownComponent), multi: true},
    {provide: NG_VALIDATORS, useExisting: forwardRef(() => DropdownComponent), multi: true},
  ],
})
export class DropdownComponent extends BaseControlDirective {
  @Input() set options(values: any) {
    if (values && values.length) {
      this.innerOptions = values;
      this.filteredOptions = [...values];
      this.chooseOptionById(values);
    }
  }

  get options(): any {
    return this.innerOptions;
  }

  @Input() isOptionObject: boolean; // {id: any, name: string}
  @Input() isLoading: boolean;
  @Input() readonly = true;
  @Input() radioButtons: boolean;
  @Input() label: string;
  @Input() transparent: boolean;
  @Input() hasCustomTemplate: boolean;
  @Input() fullWidth: boolean;
  @Input() showDescription: boolean;
  @Input() suppressObjectDescription: boolean;
  @Input() chosenOptionId: string;
  @Input() highlightFocusedInput: boolean;
  @Input() firstOptionIsSelectedByDefault: boolean;
  @Input() dropDownWrapperClasses: string;

  @Output() chosenOption: EventEmitter<any> = new EventEmitter();
  @Output() tabEnterEvent: EventEmitter<void> = new EventEmitter();
  @Output() dropdownOpened: EventEmitter<void> = new EventEmitter();
  @Output() dropdownClosed: EventEmitter<void> = new EventEmitter();

  innerOptions: any = [];
  filteredOptions: any = [];

  showDropdown = false;
  clickOutside = false;
  isFocused = false;

  currentOptionName: string;
  currentOptionDescription: string;

  focusedOptionIndex = -1; // should be always -1 till assigned to focused option

  @ViewChild('dropdownInputWrapper') dropdownInputWrapper;
  @ViewChild('dropdownInput') dropdownInput;
  @ViewChild('dropdownScrollContainer') dropdownScrollContainer;
  @ViewChild('optionsListContainer') optionsListContainer;
  @ViewChild('optionsListComponent') optionsListComponent: OptionsListComponent;
  @ViewChildren('dropdownItems') dropdownItemsQueryList: QueryList<ElementRef>;

  @HostListener('document:click', ['$event'])
  documentClick(event: any): void {
    this.clickOutside =
      !this.dropdownInputWrapper?.nativeElement.contains(event.target) &&
      event.target?.className !== 'dropdown-chevron-icon';

    if (this.clickOutside && this.showDropdown) {
      this.showDropdown = false;
      this.dropdownClosed.emit();
    }
  }

  @HostListener('keydown', ['$event'])
  keyDown(ev): void {
    if (ev.key === 'ArrowUp') {
      this.arrowNavigationHandler(-1);
    } else if (ev.key === 'ArrowDown') {
      this.arrowNavigationHandler(+1);
    } else if (ev.key === 'Enter' && !this.showDropdown) {
      ev.preventDefault();
      this.showDropdown = true;
    } else if (ev.key === 'Enter' || (ev.key === 'Tab' && this.hasCustomTemplate) || ev.key === ' ') {
      setTimeout(() => {
        if (this.focusedOptionIndex >= 0 && this.focusedOptionIndex < this.options.length) {
          this.chooseOption(this.options[this.focusedOptionIndex]);
        }

        if (this.isFocused) {
          this.showDropdown = false;
          this.tabEnterEvent.emit();
        }
      }, 100);
    }
  }

  // TODO: fix write dropdown control duplication
  writeValue(v: any): void {
    super.writeValue(v);

    if (this.options) {
      this.currentOptionName = v !== undefined ? (this.isOptionObject ? this.options.find(el => el.id === v)?.name : v) : '';
    }
  }

  inputClick(): void {
    this.showDropdown = !this.showDropdown;

    if (this.showDropdown) {
      this.dropdownOpened.emit();
    } else this.dropdownClosed.emit();

    if (this.radioButtons && this.filteredOptions?.length) {
      setTimeout(() => this.optionsListComponent?.arrowNavigationHandler(+1));
    }
  }

  chooseOption(option: any): void {
    this.initWithChosenOption(option);
    this.chosenOption.emit(option);
    this.showDropdown = false;
  }

  initWithChosenOption(option: any): void { 
    this.value = this.isOptionObject ? option.id : option;
    this.currentOptionName = this.isOptionObject ? option.name : option;
    this.currentOptionDescription = this.isOptionObject ? option.description : option;
  }

  afterOptionTabEvent(): void {
    if (!this.hasCustomTemplate) {
      this.showDropdown = false;
      this.tabEnterEvent.emit();
    }
  }

  toggleDropdown(ev: Event): void {
    ev.stopPropagation();
    this.showDropdown = !this.showDropdown;

    if (this.showDropdown) {
      this.dropdownOpened.emit();
    } else this.dropdownClosed.emit();

  }

  private chooseOptionById(values: any[]): void {
    if (this.chosenOptionId) {
      const value = values.find(x => x.id === this.chosenOptionId);

      if (value) {
        this.chooseOption(value);
      }
    }
  }

  private arrowNavigationHandler(increment: number): void {
    if ((this.optionsListContainer || this.dropdownScrollContainer) && !this.hasCustomTemplate) {
      let queryList;

      if (this.radioButtons && this.optionsListContainer) {
        queryList = this.optionsListComponent.optionsQueryList.toArray();
      } else if (!this.radioButtons && this.dropdownScrollContainer) {
        queryList = this.dropdownItemsQueryList.toArray();
      }

      if (queryList?.length) {
        this.focusedOptionIndex += increment;

        if (this.focusedOptionIndex < -1 || this.focusedOptionIndex === queryList.length) {
          this.focusedOptionIndex = 0;
        } else if (this.focusedOptionIndex === -1) {
          this.focusedOptionIndex = queryList.length - 1;
        }

        setTimeout(() => queryList[this.focusedOptionIndex].nativeElement.focus());

        if (this.focusedOptionIndex > 0 && this.focusedOptionIndex < queryList.length - 1) {
          queryList[this.focusedOptionIndex].nativeElement.scrollIntoView({behavior: 'smooth', block: 'center'});
        } else if (this.focusedOptionIndex === 0) {
          setTimeout(() => (this.optionsListContainer || this.dropdownScrollContainer).nativeElement.scrollTop = 0, 50);
        } else {
          setTimeout(() => (this.optionsListContainer || this.dropdownScrollContainer).nativeElement.scrollTop =
            (this.optionsListContainer || this.dropdownScrollContainer)
              .nativeElement.scrollHeight - queryList[this.focusedOptionIndex].nativeElement.clientHeight, 50);
        }
      }
    }
  }
  
  focus(): void {
    setTimeout(() => {
     this.dropdownInput.nativeElement.focus(); 
    });
  }

  reset(): void {
    this.value = undefined;
    this.currentOptionName = undefined;
    this.currentOptionDescription = undefined;
  }
}
